/**
 * Certificat Qualiopi et mentions publiques obligatoires (04/11/2026, écarts E11 et E12 du rapport Formatrix → S4M).
 *
 * Fonctions pures : elles mettent en forme ce que l'administrateur a SAISI (Organisme → Qualiopi & vitrine). Aucune
 * valeur n'est inventée : un champ vide donne `null`, et la vitrine affiche « À compléter ».
 *
 * Sources des formules :
 *  - déclaration d'activité : Code du travail, art. L6352-12 et R6351-6 (« Enregistré sous le numéro … auprès du préfet
 *    de région … Cet enregistrement ne vaut pas agrément de l'État ») ;
 *  - marque Qualiopi : règlement d'usage de la marque (ministère du Travail) — le logo est accompagné de la mention de la
 *    ou des catégories d'actions au titre desquelles la certification a été délivrée (art. L6313-1 du Code du travail).
 */

export const CATEGORIES_QUALIOPI = [
  { code: "actions_formation", libelle: "Actions de formation" },
  { code: "bilan_competences", libelle: "Bilans de compétences" },
  { code: "vae", libelle: "Actions permettant de faire valider les acquis de l'expérience" },
  { code: "apprentissage", libelle: "Actions de formation par apprentissage" },
] as const;
export type CategorieQualiopi = (typeof CATEGORIES_QUALIOPI)[number]["code"];
export const CODES_CATEGORIES = CATEGORIES_QUALIOPI.map((c) => c.code) as unknown as readonly [CategorieQualiopi, ...CategorieQualiopi[]];

/** Alerte d'échéance du certificat : 90 jours avant la fin de validité (hypothèse de la nuit du 04/11/2026, à confirmer). */
export const ALERTE_ECHEANCE_JOURS = 90;

export type EtatCertificat = "non_saisi" | "valide" | "a_renouveler" | "echu";

const JOUR_MS = 24 * 60 * 60 * 1000;
const estDateIso = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d) && !Number.isNaN(Date.parse(`${d}T00:00:00Z`));

/** État du certificat au jour `aujourdhui` (AAAA-MM-JJ), d'après sa date de fin de validité. */
export function etatCertificat(dateFin: string, aujourdhui: string): { etat: EtatCertificat; jours_restants: number | null } {
  if (!estDateIso(dateFin) || !estDateIso(aujourdhui)) return { etat: "non_saisi", jours_restants: null };
  const jours = Math.round((Date.parse(`${dateFin}T00:00:00Z`) - Date.parse(`${aujourdhui}T00:00:00Z`)) / JOUR_MS);
  if (jours < 0) return { etat: "echu", jours_restants: jours };
  return { etat: jours <= ALERTE_ECHEANCE_JOURS ? "a_renouveler" : "valide", jours_restants: jours };
}

/** Contrôle de cohérence des dates saisies (messages affichés tels quels). */
export function erreursDatesCertificat(emission: string, fin: string): Record<string, string> {
  const e: Record<string, string> = {};
  if (emission && !estDateIso(emission)) e.of_qualiopi_date_emission = "Date attendue au format AAAA-MM-JJ.";
  if (fin && !estDateIso(fin)) e.of_qualiopi_date_fin = "Date attendue au format AAAA-MM-JJ.";
  if (!e.of_qualiopi_date_emission && !e.of_qualiopi_date_fin && emission && fin && fin <= emission)
    e.of_qualiopi_date_fin = "La fin de validité doit suivre la date d'émission.";
  return e;
}

/** Formule de la déclaration d'activité, ou `null` tant que le numéro ou la région manque. */
export function mentionDeclarationActivite(numero: string, region: string): string | null {
  const n = numero.trim();
  const r = region.trim();
  if (!n || !r) return null;
  return `Enregistré sous le numéro ${n} auprès du préfet de région ${r}. Cet enregistrement ne vaut pas agrément de l'État.`;
}

export const libelleCategorie = (c: string) => CATEGORIES_QUALIOPI.find((x) => x.code === c)?.libelle ?? null;

/** Catégories connues, dans l'ordre du référentiel, sans doublon. */
export function categoriesValides(brut: unknown): CategorieQualiopi[] {
  const l = Array.isArray(brut) ? brut.filter((x): x is string => typeof x === "string") : [];
  return CATEGORIES_QUALIOPI.map((c) => c.code).filter((c) => l.includes(c));
}

/** Mention placée près du logo Qualiopi, ou `null` si aucune catégorie n'est saisie. */
export function mentionQualiopi(categories: unknown): string | null {
  const c = categoriesValides(categories);
  if (c.length === 0) return null;
  const libelles = c.map((x) => libelleCategorie(x)!.toUpperCase());
  return c.length === 1
    ? `La certification qualité a été délivrée au titre de la catégorie d'action suivante : ${libelles[0]}.`
    : `La certification qualité a été délivrée au titre des catégories d'actions suivantes : ${libelles.join(" ; ")}.`;
}
